import React, { useState } from 'react'
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../../../components/GenericModal'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../../components/Notifications'
import { notifyError } from '../../../../utils/notifyError'
import { DistributionManager } from '../../../../APIManager'
import { SlotStatus } from '../../../../models/DistributionPlan'
import { toDate } from 'date-fns-tz'
import { describeInUserTimeZone } from '../../../../helpers/formatDateByUserTimeZone'
import { REOPEN_MIN_LEAD_MS } from '../../../ProjectCourseFeature/Courses/reopenDateRules'
import { grey } from '../../../../colors'

export interface ChangeSurveyDatesModalProps {
  isOpen: boolean
  onClose: () => void
  slotId: string
  sectionId: string
  /** Every abbreviation the card carries — one section, or the whole cross-listed group. */
  sectionAbbr: string
  slotNodeStatus: string
  startTimestamp: string
  endTimestamp: string
  /** Rolling-results projects have no per-section release date, so none is shown or derived. */
  isRollingResultsEnabled?: boolean
  /**
   * Grade-posting release mode (series-level). When on, section results release is an admin
   * action taken after grades post, so there is no derived release instant to promise here —
   * the card replaces the release date with grade-posting messaging for the same reason.
   */
  isInstructorReleaseOnGradePostingEnabled?: boolean
  /**
   * Whether the cross-list fan-out would reach anything — the payload's
   * `canApplyToCrossListed`, not `isCrosslistedSection`. The institutional cross-listing setting
   * can leave a section with a code that still only sees itself, where the option would promise
   * something the write cannot do.
   */
  canApplyToCrossListed?: boolean
  /**
   * The survey project's own window and release ceiling, from the dashboard payload (BE-007).
   *
   * The release one is the payload's `slotReleaseTimestamp`, not its `releaseTimestamp` — that
   * name carries a role-dependent per-instructor value, and a message string in grade-posting
   * mode. Each is optional because a project may have no release date and the older payload had
   * none of them: absent means the matching rule is left to the server, which enforces it either
   * way and whose refusal the modal shows verbatim. Never fetched per card.
   */
  projectStartTimestamp?: string | null
  projectEndTimestamp?: string | null
  projectReleaseTimestamp?: string | null
}

interface FormValues {
  startTimestamp?: Date | string | null
  endTimestamp: Date | string
  applyToCrossListed: boolean
}

const ONE_HOUR_MS = 60 * 60 * 1000
const ONE_DAY_MS = 24 * ONE_HOUR_MS
/**
 * The backend's lead time, imported rather than restated: the same floor is already shared by
 * the per-row and bulk reopen forms, and its own comment explains why one copy is the rule.
 */
const MIN_LEAD_MS = REOPEN_MIN_LEAD_MS

const toIsoString = (value: Date | string): string => (value instanceof Date ? value.toISOString() : new Date(value).toISOString())

const asTime = (value: Date | string | null | undefined): number | null => {
  if (value == null || value === '') return null
  // Strings off the wire are UTC-**naive** — Marshmallow dumps a naive datetime as
  // `2026-12-10T23:59:00`, with no offset — and native `new Date()` reads that form as *local*
  // time. Everything else in this file already assumes UTC (`GenericDateTimePicker` renders via
  // `dayjs.utc`, `formatDateByUserTimeZone` via `toDate(..., { timeZone: 'UTC' })`), so parsing
  // natively here would shift every payload-derived bound by the browser's offset: five hours in
  // New York, enough to refuse a legal reopen or accept one the server then refuses.
  // `toDate` still honours an explicit `Z` or offset when the string carries one.
  const time = value instanceof Date
    ? value.getTime()
    : toDate(value, { timeZone: 'UTC' }).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * The same parse, as a `Date` — for the form's own defaults.
 *
 * Handing yup a wire string instead would have it cast the value itself, and yup reads a
 * timestamp with no offset as *local* ("timestamps without timezone identifiers should be
 * considered local time", `yup/index.js`). `asTime` reads it as UTC, so an untouched field and
 * the `currentStart`/`currentEnd` it is compared against would sit a browser offset apart: a start
 * nobody moved counts as edited and gets submitted, and the end — always submitted — goes to the
 * server five hours out in New York. Casting once, here, keeps every later comparison honest.
 * Display is unaffected: the picker renders `dayjs.utc(value)`, which is the same instant either
 * way. Found by review on #1936.
 */
const asDate = (value: Date | string | null | undefined): Date | null => {
  const time = asTime(value)
  return time == null ? null : new Date(time)
}

/**
 * Every refusal names the value that would be accepted, matching the error-copy standard the
 * endpoint's own messages follow — otherwise the same rule speaks in two voices depending on
 * whether the client or the server caught it.
 */
const earliestMessage = (field: 'start' | 'end', boundary: number): string =>
  `The ${field} date must be inside the survey project window — the earliest you can set is ${describeInUserTimeZone(new Date(boundary))}.`

const latestMessage = (field: 'start' | 'end', boundary: number): string =>
  `The ${field} date must be inside the survey project window — the latest you can set is ${describeInUserTimeZone(new Date(boundary))}.`

/**
 * The end-date boundaries a 422 carries, if it carries any.
 *
 * Marshmallow keys the sentence under the field and puts the acceptable value beside it; the
 * response passes through `axios-case-converter`, so the keys arrive camelCased. Only the end
 * boundaries are read — the start is either locked or, on Published, bounded by its own rule.
 *
 * `data` before `response.data`, and both are checked: the shared client's response interceptor
 * rejects with a **restructured** object — `{ message, data, status, ... }` with no `response`
 * at all (`src/utils/api.ts`) — so reading only `response.data` finds nothing in the app while
 * still passing a test that fabricates a raw Axios error.
 */
const readEndBoundaries = (error: unknown): { earliestEnd?: number, latestEnd?: number } => {
  const wrapper = error as { data?: Record<string, unknown>, response?: { data?: Record<string, unknown> } }
  const body = wrapper?.data ?? wrapper?.response?.data
  if (body == null || typeof body !== 'object') return {}
  const earliest = asTime(body.earliestAllowedEndTimestamp as string | undefined)
  const latest = asTime(body.latestAllowedEndTimestamp as string | undefined)
  return {
    ...(earliest != null ? { earliestEnd: earliest } : {}),
    ...(latest != null ? { latestEnd: latest } : {})
  }
}

/** How far after the new end date the backend places both section release timestamps. */
const releaseOffsetMs = (isClosed: boolean): number => (isClosed ? ONE_DAY_MS : ONE_HOUR_MS)

/**
 * Client-side mirrors of the endpoint's date rules 7-12. Mirrors, not the source of truth: the
 * server re-checks every one of them, and rules that need the project window are skipped when the
 * payload does not carry it rather than guessed at.
 */
const buildValidationSchema = (
  { isPublished, isClosed, isRollingResults, currentStart, currentEnd, projectStart, projectEnd, projectRelease }: {
    isPublished: boolean
    isClosed: boolean
    isRollingResults: boolean
    currentStart: number | null
    currentEnd: number | null
    projectStart: number | null
    projectEnd: number | null
    projectRelease: number | null
  }
  // `AnyObjectSchema` rather than `ObjectSchema<FormValues>`: `startTimestamp` is built with
  // `.when()`, whose branches yup types independently, so a `FormValues`-parameterised return
  // only type-checks behind a cast — which would assert the shape rather than verify it. The
  // resolver validates against `FormValues` at the `useForm` call below, which is where the
  // form's shape is actually enforced.
): yup.AnyObjectSchema => yup.object({
  startTimestamp: yup
    .date()
    .nullable()
    .when([], {
      // Rule 9 applies to Published sections only; on Live and Closed the field is read-only text
      // and never submitted, so there is nothing to validate.
      is: () => isPublished,
      then: (schema) => schema
        .typeError('A start date is required.')
        .required('A start date is required.')
        .test('start-at-least-an-hour-out', '', function (value) {
          const start = asTime(value)
          // Only a *changed* start has to clear the floor. The field is pre-filled with the
          // section's current start, and on a survey that opens in forty minutes that value is
          // already inside the hour — validating it would block an end-date change the
          // instructor is entitled to make, on a field they never touched.
          if (start === currentStart) return true
          const floor = Date.now() + MIN_LEAD_MS
          if (start != null && start >= floor) return true
          return this.createError({
            message: `The start date must be at least an hour from now — the earliest you can set is ${describeInUserTimeZone(new Date(floor))}.`
          })
        })
        .test('start-inside-project-window', '', function (value) {
          const start = asTime(value)
          if (start == null) return false
          // Same exemption its floor-side sibling above carries, and the one the backend added in
          // its Approved Behavior Change #8: section survey dates are derived from the section's
          // own dates plus an offset with no clamp to the project window, so a stored start before
          // the project's own start is ordinary importer output. Judging a value the instructor
          // never touched — and which is not submitted — refuses every end-date change on that
          // section, with an error naming a field this modal does not even show.
          if (start === currentStart) return true
          if (projectStart != null && start < projectStart) {
            return this.createError({ message: earliestMessage('start', projectStart) })
          }
          if (projectEnd != null && start > projectEnd) {
            return this.createError({ message: latestMessage('start', projectEnd) })
          }
          return true
        }),
      otherwise: (schema) => schema.notRequired()
    }),
  endTimestamp: yup
    .date()
    .typeError('An end date is required.')
    .required('An end date is required.')
    .test('end-at-least-an-hour-out', '', function (value) {
      const end = asTime(value)
      const floor = Date.now() + MIN_LEAD_MS
      if (end != null && end >= floor) return true
      return this.createError({
        message: `The end date must be at least an hour from now — the earliest you can set is ${describeInUserTimeZone(new Date(floor))}.`
      })
    })
    .test('end-an-hour-after-start', '', function (value) {
      const end = asTime(value)
      if (end == null) return false
      // On Live and Closed the start is locked, so the section's current start is the comparison.
      const start = isPublished ? asTime((this.parent as FormValues).startTimestamp) : currentStart
      if (start == null || end >= start + ONE_HOUR_MS) return true
      return this.createError({
        message: `The end date must be at least an hour after the start date — the earliest you can set is ${describeInUserTimeZone(new Date(start + ONE_HOUR_MS))}.`
      })
    })
    .test('end-inside-project-window', '', function (value) {
      const end = asTime(value)
      if (end == null) return false
      if (projectStart != null && end < projectStart) {
        return this.createError({ message: earliestMessage('end', projectStart) })
      }
      if (projectEnd != null && end > projectEnd) {
        return this.createError({ message: latestMessage('end', projectEnd) })
      }
      return true
    })
    .test('end-under-release-ceiling', '', function (value) {
      // Rule 11. The ceiling moves with the release offset, so a Closed reopen is bounded a day
      // back from the project release date where a Live section is bounded an hour back.
      if (isRollingResults || projectRelease == null) return true
      const end = asTime(value)
      const ceiling = projectRelease - releaseOffsetMs(isClosed)
      if (end != null && end <= ceiling) return true
      return this.createError({
        message: `The end date is too late for this project's results release date — the latest you can set is ${describeInUserTimeZone(new Date(ceiling))}.`
      })
    })
    .test('closed-sections-extend-only', '', function (value) {
      // Rule 12. Published and Live may pull the end date in; only Closed is extend-only, because
      // there the end-date change is what reopens the section.
      if (!isClosed || currentEnd == null) return true
      const end = asTime(value)
      if (end != null && end > currentEnd) return true
      return this.createError({
        message: `A closed survey can only be reopened with a later end date — later than ${describeInUserTimeZone(new Date(currentEnd))}.`
      })
    }),
  applyToCrossListed: yup.boolean().required()
})

/**
 * Lets an instructor move their own section's survey window (CR-337).
 *
 * Three variants off the section's status, because the rules differ: Published edits both dates,
 * Live edits the end date only, and Closed edits the end date forward only — where saving is a
 * reopen, not a date edit. A sibling of `SurveyProjectReopenModal`, not a refactor of it.
 */
const ChangeSurveyDatesModal: React.FC<ChangeSurveyDatesModalProps> = ({
  isOpen,
  onClose,
  slotId,
  sectionId,
  sectionAbbr,
  slotNodeStatus,
  startTimestamp,
  endTimestamp,
  isRollingResultsEnabled = false,
  isInstructorReleaseOnGradePostingEnabled = false,
  canApplyToCrossListed = false,
  projectStartTimestamp,
  projectEndTimestamp,
  projectReleaseTimestamp
}) => {
  const queryClient = useQueryClient()
  // Boundaries the server sent back with a refusal. They re-bound the picker, so a refused
  // date stops being offerable — otherwise the instructor can resubmit the same value and be
  // refused again for the same reason.
  const [serverBounds, setServerBounds] = useState<{ earliestEnd?: number, latestEnd?: number }>({})

  const isClosed = slotNodeStatus === SlotStatus.Closed
  const isPublished = slotNodeStatus === SlotStatus.Published
  const currentStart = asTime(startTimestamp)
  const currentEnd = asTime(endTimestamp)

  const methods = useForm<FormValues>({
    resolver: yupResolver(buildValidationSchema({
      isPublished,
      isClosed,
      isRollingResults: isRollingResultsEnabled,
      currentStart,
      currentEnd,
      projectStart: asTime(projectStartTimestamp),
      projectEnd: asTime(projectEndTimestamp),
      projectRelease: asTime(projectReleaseTimestamp)
    })),
    defaultValues: {
      startTimestamp: asDate(startTimestamp),
      // Falls back to the raw value only when it does not parse — an empty or malformed end is
      // left for yup to refuse with "An end date is required.", as it did before.
      endTimestamp: asDate(endTimestamp) ?? endTimestamp,
      applyToCrossListed: false
    }
  })

  const { control, handleSubmit } = methods
  const watchedEnd = useWatch({ control, name: 'endTimestamp' })

  const { mutate: saveSurveyDates, isPending: isSaving } = useMutation({
    mutationFn: async (values: FormValues) => {
      return await DistributionManager.updateSectionSurveyDates({
        slotId,
        sectionId,
        endTimestamp: toIsoString(values.endTimestamp),
        // Published only, and only when the instructor actually moved it. Echoing an unchanged
        // start back makes the server apply its own start floor to a value nobody edited, which
        // refuses end-date changes on a section that opens within the hour.
        ...(isPublished && values.startTimestamp != null && asTime(values.startTimestamp) !== currentStart
          ? { startTimestamp: toIsoString(values.startTimestamp) }
          : {}),
        // Only a cross-listed card asked the question, so only it answers it.
        ...(canApplyToCrossListed ? { applyToCrossListed: values.applyToCrossListed } : {})
      })
    },
    onSuccess: async (response) => {
      // `reopened` is the server's own account of what the save did. It beats the status the card
      // was rendered with, which can be stale: a section that closed on schedule between page
      // load and save is reopened by this request while the card still says Live.
      const reopened = response?.data?.reopened ?? isClosed
      notification(
        response?.data?.message ??
        (reopened === true
          ? `${sectionAbbr} has been reopened with a new end date.`
          : `Survey dates updated for ${sectionAbbr}.`),
        { variant: 'success' }
      )
      await queryClient.invalidateQueries({ queryKey: ['instructorDistributions'] })
      onClose()
    },
    onError: (error: unknown) => {
      // The server's own sentence names the value that would be accepted, so it is shown as-is
      // rather than replaced with a generic failure.
      // Keyed by field, not left to `notifyError`'s "first readable value" scan: a Marshmallow 422
      // carries none of its priority keys, so whichever key the body happens to serialize first
      // wins — and a boundary key arriving first would toast a bare ISO timestamp.
      notifyError(error, {
        messageKey: ['endTimestamp', 'startTimestamp', 'slotStatus'],
        fallbackMessage: 'Failed to update survey dates. Please try again.'
      })
      // Merged, not replaced: a refusal names only the boundary its own rule broke, so a second
      // 422 carrying `earliestAllowedEndTimestamp` would otherwise drop the ceiling the first one
      // taught us and the picker would re-offer a date the server has already refused. Absent
      // keys are omitted by `readEndBoundaries`, so nothing here erases what it does not carry.
      setServerBounds((previous) => ({ ...previous, ...readEndBoundaries(error) }))
    },
    // The provider's mutation-cache subscriber notifies on any unsilenced mutation error, so
    // without this the same refusal is toasted twice.
    meta: { silent: true }
  })

  const handleFormSubmit = (values: FormValues): void => {
    saveSurveyDates(values)
  }

  // What the backend will set both section release timestamps to, shown live so an instructor sees
  // their results release move rather than discovering it afterwards.
  const derivedRelease = (() => {
    const end = asTime(watchedEnd)
    return end == null ? '' : describeInUserTimeZone(new Date(end + releaseOffsetMs(isClosed)))
  })()

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={isClosed ? `Reopen Survey for ${sectionAbbr}` : `Change Survey Dates for ${sectionAbbr}`}
      maxWidth='sm'
      action={false}
    >
      <FormProvider {...methods}>
        {/* `handleSubmit` returns a promise where `onSubmit` expects void. react-hook-form owns
            that promise and its rejection, so this is the library's contract rather than an
            unhandled error — the same disable, for the same reason, as `SurveyProjectReopenModal`. */}
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Box display='flex' flexDirection='column' gap={4}>
            {isClosed && (
              <Alert severity='warning'>
                This survey has closed. Saving a later end date <strong>reopens it immediately</strong> and
                students can respond again.
              </Alert>
            )}

            {isPublished
              ? (
                <GenericDateTimePicker
                  name='startTimestamp'
                  label='Start Date'
                  required
                  minDate={new Date(Date.now() + MIN_LEAD_MS)}
                  maxDate={asTime(projectEndTimestamp) != null ? new Date(asTime(projectEndTimestamp) as number) : undefined}
                />
                )
              : (
                <Box>
                  <Typography fontSize='14px' fontWeight='600' color={grey[950]}>Start Date</Typography>
                  <Typography fontSize='14px' color={grey[700]}>
                    {describeInUserTimeZone(startTimestamp)}
                  </Typography>
                  <Typography fontSize='12px' color={grey[700]}>
                    The start date can&apos;t be changed once a survey has opened.
                  </Typography>
                </Box>
                )}

            <GenericDateTimePicker
              name='endTimestamp'
              label='End Date'
              required
              // `minDate`/`maxDate` bound the calendar to whole days, so they narrow which days
              // are reachable rather than which instants; the yup rules above are what refuse an
              // out-of-range time on an in-range day. Both are wanted: fewer wrong clicks, and a
              // message naming the value that would be accepted when one gets through.
              minDate={new Date(Math.max(
                Date.now() + MIN_LEAD_MS,
                // A closed survey can only be reopened forward. The floor is its current end, not
                // an hour past it — rule 12 asks for later, and an extra hour here would refuse a
                // legal reopen to thirty minutes after the old close.
                isClosed && currentEnd != null ? currentEnd : 0,
                serverBounds.earliestEnd ?? 0
              ))}
              maxDate={(() => {
                const projectEnd = asTime(projectEndTimestamp)
                const projectRelease = asTime(projectReleaseTimestamp)
                const ceilings = [
                  projectEnd,
                  isRollingResultsEnabled || projectRelease == null ? null : projectRelease - releaseOffsetMs(isClosed),
                  // Whatever the server said when it refused the last attempt, which may be a
                  // rule this client cannot see for want of a payload field.
                  serverBounds.latestEnd ?? null
                ].filter((candidate): candidate is number => candidate != null)
                return ceilings.length > 0 ? new Date(Math.min(...ceilings)) : undefined
              })()}
            />

            <Typography fontSize='12px' color={grey[700]}>
              Results already visible for this section will be hidden until the survey closes again.
            </Typography>

            {isInstructorReleaseOnGradePostingEnabled && (
              <Box>
                <Typography fontSize='14px' fontWeight='600' color={grey[950]}>Results Release Date</Typography>
                <Typography fontSize='12px' color={grey[700]}>
                  Your administrator releases results after grades are posted, so changing these
                  dates does not move when you see results.
                </Typography>
              </Box>
            )}

            {!isRollingResultsEnabled && !isInstructorReleaseOnGradePostingEnabled && (
              <Box>
                <Typography fontSize='14px' fontWeight='600' color={grey[950]}>Results Release Date</Typography>
                <Typography fontSize='14px' color={grey[700]}>
                  {derivedRelease === '' ? 'Set from the end date' : derivedRelease}
                </Typography>
                <Typography fontSize='12px' color={grey[700]}>
                  Set automatically from the end date — {isClosed ? 'one day' : 'one hour'} after the survey closes.
                </Typography>
              </Box>
            )}

            {canApplyToCrossListed && (
              <FormControlLabel
                control={
                  <Checkbox
                    // Unchecked by default: the instructor decides, and on a closed survey the
                    // choice is who gets reopened, not just whose dates move.
                    {...methods.register('applyToCrossListed')}
                    defaultChecked={false}
                  />
                }
                label={
                  <Typography fontSize='14px'>
                    {/* "all", not "the other": the card carries one combined label for the whole
                        group, so the names listed include this section too. */}
                    {isClosed
                      ? `Also reopen all cross-listed sections (${sectionAbbr})`
                      : `Also apply these dates to all cross-listed sections (${sectionAbbr})`}
                  </Typography>
                }
              />
            )}
          </Box>

          <DefaultModalActions
            confirmButtonProps={{
              text: isClosed ? 'Reopen Survey' : 'Save',
              type: 'submit',
              loading: isSaving,
              // `loading` alone leaves a submit button that still submits: the loading state
              // renders an icon button carrying `type="submit"`, so a second click sends a second
              // PATCH — a second reopen, on the Closed variant.
              disabled: isSaving
            }}
            destructiveButtonProps={{
              onClick: onClose,
              disabled: isSaving
            }}
          />
        </form>
      </FormProvider>
    </GenericModal>
  )
}

export default ChangeSurveyDatesModal
